import { LCAttributeFilter } from '../actions/LiveChatCRUDActions';
import JSONUtils from '../helpers/json-utils';
import { Variable } from '../interfaces/audiences';
import CompanyUsersModel from './CompanyUsersModel';
import { LiveChatChannel } from './LivechatConversationModel';
import {
  LivechatConversationState,
  LivechatConversationStateList,
} from './LivechatConversationStateModel';

type AssigneeFilterSetting = number | 'unassigned' | 'all';

export type LivechatSavedFilterStatePerPageModel = {
  conversationFilterState: LivechatConversationState;
  assigneeFilterSetting: AssigneeFilterSetting;
  selectedChannels?: LiveChatChannel[];
  selectedTags?: string[];
  selectedAttribute?: LCAttributeFilter | null;
};
export type LivechatSavedFilterStateModel = {
  [remoteFbPageId: string]: LivechatSavedFilterStatePerPageModel;
};

export const LivechatSavedFilterStatePerPageDefault = {
  assigneeFilterSetting: 'all',
  conversationFilterState: 'main',
  selectedAttribute: null,
  selectedTags: [],
  selectedChannels: [],
};

export const getLivechatSavedFilterState = (
  savedData: string | undefined,
  remoteFbPageId: string | undefined,
  companyUsers: CompanyUsersModel[],
  variables: Variable[],
): LivechatSavedFilterStateModel => {
  const validatedAssigneeFilterSetting = (value: string | number) => {
    if (value === 'unassigned' || value === 'all') {
      return value;
    }
    const userId = Number(value);
    const user = companyUsers.find(u => u.id === userId);
    return user?.id || 'all';
  };

  const validatedConversationState = (value: string | undefined) => {
    if (Object.keys(LivechatConversationStateList).includes(value || '')) {
      return value as LivechatConversationState;
    }
    return 'main';
  };
  const validatedSelectedAttribute = (
    value: LCAttributeFilter | null | undefined,
  ) => {
    if (value && variables.find(v => v.variableId === value.variableId)) {
      return value;
    }
    return null;
  };
  const validatedPerPageSettings = (
    value: LivechatSavedFilterStatePerPageModel,
  ) => {
    return {
      ...value,
      conversationFilterState: validatedConversationState(
        value.conversationFilterState,
      ),
      assigneeFilterSetting: validatedAssigneeFilterSetting(
        value.assigneeFilterSetting,
      ),
      selectedAttribute: validatedSelectedAttribute(value.selectedAttribute),
    } as LivechatSavedFilterStatePerPageModel;
  };
  const parseJSONSavedLCFilterState: (
    value: string,
  ) => LivechatSavedFilterStateModel = value => {
    const parsedValue =
      JSONUtils.safeParse<LivechatSavedFilterStateModel>(value);
    if (!parsedValue) {
      return {};
    }
    for (const key of [remoteFbPageId, 'default']) {
      if (key && parsedValue[key]) {
        parsedValue[key] = validatedPerPageSettings(parsedValue[key]);
      }
    }
    return parsedValue;
  };

  if (!savedData) {
    return {};
  }

  if (savedData[0] !== '{') {
    // old non-json value
    return {
      default: {
        conversationFilterState:
          validatedConversationState(savedData) || 'main',
        assigneeFilterSetting: 'all',
      },
    };
  }

  return parseJSONSavedLCFilterState(savedData) || {};
};
